import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link, useParams } from 'react-router-dom';
import { fetchAllDataAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { numberFormat } from '../components/numberFormat';
import { FaArrowLeft, FaPhone, FaBoxOpen } from 'react-icons/fa';
import { MdLocalShipping } from 'react-icons/md';
import withAuth from '../utils/withAuth';

const SHIP_STYLE = {
    'Delivered':     { color: '#0e7c52', bg: '#e8f5e9' },
    'RTO Delivered': { color: '#597e0d', bg: '#f1f8e9' },
    'Cancelled':     { color: '#c62828', bg: '#ffebee' },
    'In Transit':    { color: '#1565c0', bg: '#e3f2fd' },
    'default':       { color: '#555',    bg: '#f5f5f5' },
};

const RetentionCustomer = () => {
    const { mobile }          = useParams();
    const [history, setHistory] = useState([]);
    const [isLoading, setLoading] = useState(true);

    // ── Summary calculated from history ──────────────────────────
    const totalOrders    = history.length;
    const totalLTV       = history.reduce((s, r) => s + parseFloat(r.subtotal_price || 0), 0);
    const lastOrder      = history[0] || {};
    const delivered      = history.filter(r => r.shipment_status === 'Delivered' || r.shipment_status === 'RTO Delivered').length;
    const deliveryRate   = totalOrders > 0 ? Math.round((delivered / totalOrders) * 100) : 0;

    useEffect(() => {
        if (mobile) {
            fetchAllDataAPI(`sub-agent-retention-customer/${mobile}`)
                .then(res => {
                    if (res?.data?.status === 1) setHistory(res.data.history || []);
                    else toast.warning('No history found');
                })
                .catch(() => toast.error('Failed to load customer history'))
                .finally(() => setLoading(false));
        }
    }, [mobile]);

    const getShipStyle = (status) => SHIP_STYLE[status] || SHIP_STYLE['default'];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR::CUSTOMER HISTORY</title></Helmet>
            <style>{`
                @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
                .cust-page { animation: fadeInUp 0.4s ease; }

                /* Hero */
                .cust-hero {
                    background: linear-gradient(135deg, #0d4f4f 0%, #0e7c52 100%);
                    border-radius: 16px; padding: 24px 28px; color: #fff;
                    margin-bottom: 20px; box-shadow: 0 8px 24px rgba(14,124,82,0.2);
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 16px;
                }
                .cust-hero-avatar {
                    width: 56px; height: 56px; border-radius: 14px;
                    background: rgba(255,255,255,0.15);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 22px; font-weight: 800; color: #fff;
                    flex-shrink: 0;
                }
                .cust-stats { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
                .cust-stat {
                    flex: 1; min-width: 130px; background: #fff;
                    border-radius: 14px; padding: 14px 18px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                    border-left: 4px solid transparent;
                }

                /* Timeline */
                .order-timeline { position: relative; padding-left: 24px; }
                .order-timeline::before {
                    content: ''; position: absolute; left: 7px; top: 0; bottom: 0;
                    width: 2px; background: #eee; border-radius: 2px;
                }
                .timeline-item {
                    position: relative; margin-bottom: 16px;
                    background: #fff; border-radius: 14px;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                    padding: 16px 20px; border: 1px solid #f0f0f0;
                    transition: box-shadow 0.2s;
                }
                .timeline-item:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
                .timeline-dot {
                    position: absolute; left: -20px; top: 20px;
                    width: 14px; height: 14px; border-radius: 50%;
                    border: 2px solid #fff; box-shadow: 0 0 0 2px #4e9191;
                    background: #4e9191;
                }
                .timeline-dot.delivered { background: #0e7c52; box-shadow: 0 0 0 2px #0e7c52; }
                .timeline-dot.cancelled { background: #c62828; box-shadow: 0 0 0 2px #c62828; }

                .product-chip {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: #f3e5f5; color: #6a1b9a;
                    padding: 3px 10px; border-radius: 20px;
                    font-size: 11px; font-weight: 600; margin: 2px;
                }
            `}</style>

            <ContentWrapper>
                <div className="cust-page">

                    {/* Breadcrumb */}
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav><ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                            <li className="breadcrumb-item"><Link to="/retention">Retention</Link></li>
                            <li className="breadcrumb-item">Customer History</li>
                        </ol></nav>
                    </div>

                    {/* Back button */}
                    <Link to="/retention" className="btn btn-sm mb-3"
                        style={{ background: '#f5f5f5', color: '#333', borderRadius: 10, fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <FaArrowLeft style={{ fontSize: 10 }} /> Back to Retention
                    </Link>

                    {/* Hero */}
                    <div className="cust-hero">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div className="cust-hero-avatar">
                                {(lastOrder?.cust_name || mobile || '?')[0]?.toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontSize: 20, fontWeight: 800 }}>{lastOrder?.cust_name || 'Customer'}</div>
                                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
                                    <FaPhone style={{ marginRight: 6, fontSize: 11 }} />{mobile}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                                    {lastOrder?.city}{lastOrder?.city && lastOrder?.state ? ', ' : ''}{lastOrder?.state}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Total Orders</div>
                                <div style={{ fontSize: 26, fontWeight: 800 }}>{totalOrders}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Total LTV</div>
                                <div style={{ fontSize: 26, fontWeight: 800 }}>₹{Math.round(totalLTV).toLocaleString()}</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>Delivery Rate</div>
                                <div style={{ fontSize: 26, fontWeight: 800 }}>{deliveryRate}%</div>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="cust-stats">
                        {[
                            { label: 'Total Orders',   value: totalOrders,                              color: '#4e9191', border: '#4e9191' },
                            { label: 'Delivered',      value: delivered,                                color: '#0e7c52', border: '#0e7c52' },
                            { label: 'Total LTV',      value: `₹${Math.round(totalLTV).toLocaleString()}`, color: '#1565c0', border: '#1565c0' },
                            { label: 'Avg Order Value',value: totalOrders > 0 ? `₹${Math.round(totalLTV / totalOrders).toLocaleString()}` : '₹0', color: '#6a1b9a', border: '#6a1b9a' },
                            { label: 'Last Order',     value: lastOrder?.created_date || '—',           color: '#e65100', border: '#ff9800' },
                        ].map((s, i) => (
                            <div key={i} className="cust-stat" style={{ borderLeftColor: s.border }}>
                                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Order Timeline */}
                    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', padding: 24, marginBottom: 50 }}>
                        <h6 style={{ fontWeight: 700, marginBottom: 20, color: '#1a1a2e' }}>
                            <FaBoxOpen style={{ marginRight: 8, color: '#4e9191' }} />
                            Order History ({totalOrders} orders)
                        </h6>

                        {isLoading ? (
                            <div className="text-center py-4" style={{ color: '#aaa' }}>Loading history...</div>
                        ) : history.length === 0 ? (
                            <div className="text-center py-4" style={{ color: '#bbb' }}>No orders found</div>
                        ) : (
                            <div className="order-timeline">
                                {history.map((order, i) => {
                                    const shipStyle = getShipStyle(order.shipment_status);
                                    const dotClass  = order.shipment_status === 'Delivered' ? 'delivered'
                                                    : order.shipment_status === 'Cancelled'  ? 'cancelled' : '';
                                    return (
                                        <div key={i} className="timeline-item">
                                            <div className={`timeline-dot ${dotClass}`} />

                                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                <div>
                                                    {/* Order number + date */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                        <span style={{ fontWeight: 700, fontSize: 13, color: '#4e9191', fontFamily: 'monospace' }}>
                                                            #{order.order_number}
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#888' }}>
                                                            {order.created_date} {order.created_time}
                                                        </span>
                                                    </div>

                                                    {/* Product */}
                                                    {order.product_name && (
                                                        <div style={{ marginBottom: 8 }}>
                                                            <span className="product-chip">
                                                                <MdLocalShipping /> {order.product_name}
                                                            </span>
                                                            {order.product_variant_title && (
                                                                <span className="product-chip" style={{ background: '#e3f2fd', color: '#1565c0' }}>
                                                                    {order.product_variant_title}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Address */}
                                                    <div style={{ fontSize: 12, color: '#888' }}>
                                                        {[order.city, order.state, order.pincode].filter(Boolean).join(', ')}
                                                    </div>
                                                </div>

                                                <div style={{ textAlign: 'right' }}>
                                                    {/* Amount */}
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0e7c52', marginBottom: 6 }}>
                                                        {numberFormat(order.subtotal_price)}
                                                    </div>

                                                    {/* Shipment status */}
                                                    <span style={{ background: shipStyle.bg, color: shipStyle.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                                                        {order.shipment_status || 'Pending'}
                                                    </span>

                                                    {/* AWB */}
                                                    {order.awb_number && (
                                                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, fontFamily: 'monospace' }}>
                                                            AWB: {order.awb_number}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(RetentionCustomer);